import {
  useSessionStore,
} from "~/stores/session";
import {
  checkBlockedAccessAfterApiError,
} from "~/composables/useBlockedAccess";

export type InterviewApiFetchOptions = {
  headers?: Record<string, string>;
  signal?: AbortSignal;
} & Record<string, any>;

const joinUrl = (base: string, path: string): string => {
  if (!base) {
    return path;
  }
  if (base.endsWith("/") && path.startsWith("/")) {
    return `${base.slice(0, -1)}${path}`;
  }
  if (!base.endsWith("/") && !path.startsWith("/")) {
    return `${base}/${path}`;
  }
  return `${base}${path}`;
};

export const useInterviewApiFetch = () => {
  const config = useRuntimeConfig();
  const sessionStore = useSessionStore();

  return async <T>(path: string, options: InterviewApiFetchOptions = {}): Promise<T> => {
    const {headers, signal, ...rest} = options;

    const mockFlag = String(config.public.mockApi ?? "").toLowerCase().trim();
    const mockEnabled = mockFlag === "1" || mockFlag === "true";

    const basePath = String(config.public.interviewApiBasePath ?? config.public.apiBasePath ?? "/api").trim() || "/api";

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const backendOwnedInterviewPath = normalizedPath.startsWith("/exercise/interview/presign-upload")
      || normalizedPath.startsWith("/exercise/interview/translate")
      || normalizedPath.startsWith("/exercise/interview/tts")
      || normalizedPath.startsWith("/exercise/interview/recordings");
    const baseUrl = mockEnabled
      ? ""
      : String(
          backendOwnedInterviewPath
            ? config.public.apiBaseUrl
            : (config.public.interviewApiBaseUrl ?? config.public.apiBaseUrl),
        ).trim();

    const pathWithPrefix = normalizedPath.startsWith(basePath)
      ? normalizedPath
      : joinUrl(basePath, normalizedPath);

    const url = joinUrl(baseUrl, pathWithPrefix);

    const token = await sessionStore.getAccessToken();
    const mergedHeaders: Record<string, string> = {
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
      ...(headers ?? {}),
    };

    try {
      const response = await $fetch(url, {
        headers: mergedHeaders,
        signal,
        ...rest,
      });
      return response as unknown as T;
    } catch (error: any) {
      if (signal?.aborted) {
        const abortError = new DOMException("The user aborted a request.", "AbortError");
        throw abortError;
      }
      if (error?.name === "AbortError") {
        throw error;
      }
      void checkBlockedAccessAfterApiError({
        error,
        token,
        apiBaseUrl: String(config.public.apiBaseUrl ?? "").trim(),
        apiBasePath: String(config.public.apiBasePath ?? "/api").trim() || "/api",
        mockEnabled,
        requestPath: normalizedPath,
      });
      throw error;
    }
  };
};
